import { apiGet, apiPost } from '../api-client';
import { API_BASE_URL } from '../config';
import { getTokens } from '../auth';

// ============================================================
// TypeScript Interfaces
// ============================================================

export interface ResearchSession {
  id: string;
  api_number: string;
  state: 'NM' | 'TX' | 'UT';
  status: 'pending' | 'fetching' | 'indexing' | 'ready' | 'error';
  well: string | null;
  total_documents: number;
  indexed_documents: number;
  failed_documents: number;
  error_message: string;
  failed_document_details?: Array<{
    filename: string;
    reason: string;
    message: string;
  }>;
  progress_pct: number;
  created_at: string;
  updated_at: string;
  lease_sibling_wells: Array<{
    api14: string;
    data_status: 'cold_storage' | 'indexing' | 'ready';
    operator_name: string;
    lease_name: string;
    well_number: string;
  }>;
}

export interface ResearchDocument {
  filename: string;
  url: string | null;
  file_size: string | null;
  date: string | null;
  doc_type: string | null;
}

export interface Citation {
  doc_type: string;
  section_name: string;
  excerpt: string;
  /** Visibility of the cited source ("public" | "private"); absent on old messages. */
  visibility?: 'public' | 'private' | null;
  /**
   * ExtractedDocument id of the cited source (card #117/#213). `null` for
   * vectors indexed before the id was recorded; absent on messages persisted
   * before the field existed — treat missing the same as `null`.
   */
  doc_id?: number | null;
}

export interface ResearchMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations: Citation[];
  created_at: string;
}

export interface WellSummary {
  session_id: string;
  api_number: string;
  state: string;
  document_counts: Record<string, number>;
  well_info: {
    api?: string;
    field?: string;
    lease?: string;
    county?: string;
    district?: number;
    total_depth_ft?: number;
    well_no?: string;
    operator?: string;
    operator_number?: string;
  };
  casing_records: Array<{
    string_type?: string;
    size_in?: number;
    weight_ppf?: number;
    top_ft?: number;
    bottom_ft?: number;
    hole_size_in?: number;
    source_doc_type: string;
    source_date?: string;
  }>;
  plug_records: Array<{
    plug_number?: number;
    plug_type?: string;
    method?: string;
    cement_class?: string;
    sacks?: number;
    depth_top_ft?: number;
    depth_bottom_ft?: number;
    source_doc_type: string;
    source_date?: string;
  }>;
  filing_timeline: Array<{
    document_type: string;
    date?: string;
    tracking_no?: string;
    operator?: string;
  }>;
  tubing_records?: Array<{
    size_in?: number;
    top_ft?: number | null;
    bottom_ft?: number;
    source_doc_type: string;
  }>;
  perf_records?: Array<{
    top_ft?: number;
    bottom_ft?: number;
    source_doc_type: string;
  }>;
  cement_job_records?: Array<{
    job_type?: string;
    interval_top_ft?: number | null;
    interval_bottom_ft?: number;
    sacks?: number;
    cement_top_ft?: number | null;
    source_doc_type: string;
  }>;
  tool_records?: Array<{
    tool_type?: string;
    depth_ft?: number;
    size_in?: number;
    source_doc_type: string;
  }>;
}

// ============================================================
// API Functions
// ============================================================

export async function createResearchSession(apiNumber: string, state?: string): Promise<ResearchSession> {
  const body: Record<string, string> = { api_number: apiNumber };
  if (state) body.state = state;
  return apiPost<ResearchSession>('/api/research/sessions/', body);
}

export async function getResearchSession(sessionId: string): Promise<ResearchSession> {
  return apiGet<ResearchSession>(`/api/research/sessions/${sessionId}/`);
}

export interface ExtractedDocument {
  id: number;
  document_type: string;
  source_path: string;
  neubus_filename: string;
  source_page: number | null;
  status: string;
  model_tag: string;
  created_at: string;
}

export function getDocumentPdfUrl(docId: number): string {
  return `${API_BASE_URL}/api/documents/${docId}/pdf/`;
}

/**
 * Open a Bearer-protected file URL in a new tab.
 *
 * `DocumentPDFView` (and the retrieved-document download endpoint) require an
 * `Authorization` header, so a bare `<a href>` 401s. Fetch with the access
 * token, hand the browser a blob URL, and fall back to opening the raw URL if
 * the authenticated fetch fails (matches the pre-existing Research page
 * behaviour). Absolute third-party URLs are opened directly.
 */
export function openAuthenticatedUrl(url: string): void {
  if (url.startsWith('http') && !url.startsWith(API_BASE_URL)) {
    window.open(url, '_blank');
    return;
  }
  const tokens = getTokens();
  fetch(url, {
    headers: { Authorization: `Bearer ${tokens?.access}` },
  })
    .then((res) => {
      if (!res.ok) throw new Error('Document not available');
      return res.blob();
    })
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    })
    .catch(() => {
      window.open(url, '_blank');
    });
}

/** Open an ExtractedDocument's PDF in a new tab (authenticated blob pattern). */
export function openDocumentPdf(docId: number): void {
  openAuthenticatedUrl(getDocumentPdfUrl(docId));
}

export interface RetrievedDocument {
  id: string;
  filename: string;
  kind: string;
  document_type: string | null;
  index_status: string;
  remembered: boolean;
  download_url: string;
  created_at: string;
}

export interface ResearchDocumentsResponse {
  session_id: string;
  api_number: string;
  state: string;
  total_documents: number;
  remembered_documents: number;
  indexed_documents: number;
  failed_documents: number;
  document_list: ResearchDocument[];
  extracted_documents: ExtractedDocument[];
  retrieved_documents: RetrievedDocument[];
}

export async function getResearchDocuments(sessionId: string): Promise<ResearchDocumentsResponse> {
  return apiGet<ResearchDocumentsResponse>(`/api/research/sessions/${sessionId}/documents/`);
}

export async function listResearchSessions(filters?: {
  well_api14?: string
  api_number?: string
  status?: string
}): Promise<ResearchSession[]> {
  const params = new URLSearchParams()
  if (filters?.well_api14) params.set("well_api14", filters.well_api14)
  if (filters?.api_number) params.set("api_number", filters.api_number)
  if (filters?.status) params.set("status", filters.status)
  const queryString = params.toString()
  const url = `/api/research/sessions/${queryString ? "?" + queryString : ""}`
  const resp = await apiGet<{ results: ResearchSession[] } | ResearchSession[]>(url)
  if (Array.isArray(resp)) return resp
  return (resp as { results: ResearchSession[] }).results ?? []
}

export async function getResearchSummary(sessionId: string): Promise<WellSummary> {
  return apiGet<WellSummary>(`/api/research/sessions/${sessionId}/summary/`);
}

export async function getResearchChat(sessionId: string): Promise<ResearchMessage[]> {
  const resp = await apiGet<{ session_id: string; api_number: string; messages: ResearchMessage[] }>(
    `/api/research/sessions/${sessionId}/chat/`
  );
  return resp.messages ?? [];
}

/**
 * Stream a research answer via SSE.
 * Uses fetch + ReadableStream (not EventSource) because we need JWT headers.
 */
export interface BulkSessionResult {
  api_number: string
  session_id: string | null
  state: 'TX' | 'NM' | null
  status: 'pending' | 'error'
  error: string | null
}

export interface BulkResearchSessionResponse {
  submitted: number
  sessions: BulkSessionResult[]
}

export async function createBulkResearchSessions(
  apiNumbers: string[],
  state?: 'TX' | 'NM',
): Promise<BulkResearchSessionResponse> {
  const body: Record<string, unknown> = { api_numbers: apiNumbers }
  if (state) body.state = state
  return apiPost<BulkResearchSessionResponse>('/api/research/sessions/bulk/', body)
}

export async function* streamResearchAnswer(
  sessionId: string,
  question: string,
  topK: number = 8,
  signal?: AbortSignal,
): AsyncGenerator<{ type: 'token' | 'citations' | 'done' | 'error'; content?: string; citations?: Citation[]; message?: string }> {
  const tokens = getTokens();
  const response = await fetch(`${API_BASE_URL}/api/research/sessions/${sessionId}/ask/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tokens?.access}`,
    },
    body: JSON.stringify({ question, top_k: topK }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Research query failed: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          yield data;
        } catch {
          // Skip malformed SSE lines
        }
      }
    }
  }
}
